import { type ReactNode, useEffect, useRef, useState } from 'react'
import { useQueries } from '@tanstack/react-query'

import styles from './EnterpriseDownloads.module.scss'

import Tabs from '@/components/Tabs'

const RELEASE_BASE_URL = 'https://dl.wisprflow.com'
const RELEASE_STALE_TIME_MS = 5 * 60 * 1000
const DOCUMENTATION_URL =
  'https://docs.wisprflow.ai/articles/9363440133-deploy-wispr-flow-via-mdm#download-update-policy-file-for-mac'

const WINDOWS_MSI_LATEST_URL = 'https://dl.wisprflow.ai/windows/msi/latest'

const MDM_CONFIG_BASE_URL = 'https://wispr-flow-cdn.s3.us-west-2.amazonaws.com/mdm-config-files'
const ACCESSIBILITY_PROFILE_URL = `${MDM_CONFIG_BASE_URL}/wispr-flow-accessibility.mobileconfig.zip`
const MAC_UPDATE_POLICY_URL = `${MDM_CONFIG_BASE_URL}/wispr-flow-managed-config.mobileconfig.zip`
const WIN_UPDATE_POLICY_URL = `${MDM_CONFIG_BASE_URL}/wispr-flow-set-update-frequency.ps1.zip`
const RESTORE_OWNERSHIP_SCRIPT_URL = `${MDM_CONFIG_BASE_URL}/wispr-flow-restore-app-ownership.zsh.zip`

// Fetch calls go through the Vite proxy (/wispr-flow/* → https://dl.wisprflow.com/wispr-flow/*) to avoid CORS.
// Download hrefs use the direct Cloudflare URL — <a> tags have no CORS restriction.
const RELEASE_FETCH_PATH = (path: string) => `/wispr-flow/${path}`
const RELEASE_DOWNLOAD_PATH = (path: string) => `${RELEASE_BASE_URL}/wispr-flow/${path}`

type Platform = 'win-msi' | 'mac-arm64' | 'mac-x64'
type DownloadConfig = {
  label: string
  platform: Platform
  queryKey: [string, string, string]
  queryFn: () => Promise<string>
}

const getDownloadUrl = (version: string, platform: Platform): string => {
  switch (platform) {
    case 'win-msi':
      return RELEASE_DOWNLOAD_PATH(`win32/x64/Wispr%20Flow-v${version}.msi`)
    case 'mac-arm64':
      return RELEASE_DOWNLOAD_PATH(`darwin/arm64/Wispr%20Flow-v${version}.pkg`)
    case 'mac-x64':
      return RELEASE_DOWNLOAD_PATH(`darwin/x64/Wispr%20Flow-v${version}.pkg`)
  }
}

const fetchMacVersion = async (arch: 'arm64' | 'x64'): Promise<string> => {
  const res = await fetch(RELEASE_FETCH_PATH(`darwin/${arch}/RELEASES.json`))
  if (!res.ok) {
    const error = new Error(`Failed to fetch ${arch} release info`)
    console.error('Failed to fetch Mac release info', error)
    throw error
  }
  const data = await res.json()
  return data.currentRelease as string
}

const fetchWindowsVersion = async (): Promise<string> => {
  const res = await fetch(RELEASE_FETCH_PATH('win32/x64/RELEASES'))
  if (!res.ok) {
    const error = new Error('Failed to fetch Windows release info')
    console.error('Failed to fetch Windows release info', error)
    throw error
  }
  const text = await res.text()
  const match = text.match(/WisprFlow-(\d+\.\d+\.\d+)-full\.nupkg/)
  if (!match) {
    const error = new Error('Could not parse Windows version')
    console.error('Could not parse Windows version', error)
    throw error
  }
  return match[1]
}

const DOWNLOADS: DownloadConfig[] = [
  {
    label: 'Windows MSI',
    platform: 'win-msi',
    queryKey: ['release', 'win32', 'x64'],
    queryFn: fetchWindowsVersion,
  },
  {
    label: 'Mac (Apple Silicon)',
    platform: 'mac-arm64',
    queryKey: ['release', 'darwin', 'arm64'],
    queryFn: () => fetchMacVersion('arm64'),
  },
  {
    label: 'Mac (Intel)',
    platform: 'mac-x64',
    queryKey: ['release', 'darwin', 'x64'],
    queryFn: () => fetchMacVersion('x64'),
  },
]

const TABS = ['macOS', 'Windows']
const MAC_TAB = 0
const WINDOWS_TAB = 1

const defaultTab = (): number => (/windows/i.test(navigator.userAgent) ? WINDOWS_TAB : MAC_TAB)

interface DownloadRowProps {
  label: string
  platform: Platform
  version: string | undefined
  isLoading: boolean
  isError: boolean
}

const DownloadRow = ({ label, platform, version, isLoading, isError }: DownloadRowProps) => {
  const [copiedLink, setCopiedLink] = useState<'versioned' | 'latest' | null>(null)
  const resetTimer = useRef<ReturnType<typeof setTimeout>>()

  useEffect(() => () => clearTimeout(resetTimer.current), [])

  const copyLink = async (kind: 'versioned' | 'latest', url: string) => {
    await navigator.clipboard.writeText(url)
    setCopiedLink(kind)
    clearTimeout(resetTimer.current)
    resetTimer.current = setTimeout(() => setCopiedLink(null), 2000)
  }

  return (
    <>
      <div className={styles.fileRow}>
        <span className={styles.fileLabel}>
          <span className={styles.fileLabelMain}>{label}</span>{' '}
          {version && <span className={styles.fileLabelSub}>v{version}</span>}
          {isLoading && <span className={styles.loading}>Loading version...</span>}
          {isError && <span className={styles.error}>Failed to load version</span>}
        </span>
        {version && (
          <span className={styles.fileActions}>
            <a
              className={styles.downloadLink}
              href={getDownloadUrl(version, platform)}
              target="_blank"
              rel="noopener noreferrer"
            >
              Download
            </a>
            <button
              className={styles.copyLink}
              onClick={() => copyLink('versioned', getDownloadUrl(version, platform))}
            >
              {copiedLink === 'versioned' ? 'Copied!' : 'Copy link'}
            </button>
          </span>
        )}
      </div>
      {platform === 'win-msi' && (
        <div className={styles.fileRow}>
          <span className={styles.fileLabel}>
            <span className={styles.fileLabelMain}>Stable link</span>{' '}
            <span className={styles.fileLabelSub}>always the newest version</span>
          </span>
          <span className={styles.fileActions}>
            <button className={styles.copyLink} onClick={() => copyLink('latest', WINDOWS_MSI_LATEST_URL)}>
              {copiedLink === 'latest' ? 'Copied!' : 'Copy stable link'}
            </button>
          </span>
        </div>
      )}
    </>
  )
}

interface FileRowProps {
  label: string
  sublabel: string
  href: string
}

const FileRow = ({ label, sublabel, href }: FileRowProps) => (
  <div className={styles.fileRow}>
    <span className={styles.fileLabel}>
      <span className={styles.fileLabelMain}>{label}</span> <span className={styles.fileLabelSub}>{sublabel}</span>
    </span>
    <a className={styles.downloadLink} href={href} target="_blank" rel="noopener noreferrer">
      Download
    </a>
  </div>
)

interface StepProps {
  number: number
  title: string
  children: ReactNode
}

const Step = ({ number, title, children }: StepProps) => (
  <section className={styles.step}>
    <h3 className={styles.stepTitle}>
      <span className={styles.stepNumber}>Step {number}</span>
      <span className={styles.stepDot}>·</span>
      {title}
    </h3>
    {children}
  </section>
)

const FullGuideLink = () => (
  <a className={styles.descriptionLink} href={DOCUMENTATION_URL} target="_blank" rel="noopener noreferrer">
    full guide
  </a>
)

const FrequencyHint = () => (
  <>
    Set <code className={styles.code}>UpdateFrequency</code> to your desired cadence:{' '}
    <code className={styles.code}>auto</code> (default), <code className={styles.code}>weekly</code>,{' '}
    <code className={styles.code}>bi-weekly</code>, or <code className={styles.code}>monthly</code>. Then upload it to
    your MDM.
  </>
)

interface PlatformDownloads {
  downloads: DownloadConfig[]
  results: Record<Platform, { data: string | undefined; isLoading: boolean; isError: boolean }>
}

const MacSetup = ({ downloads, results }: PlatformDownloads) => (
  <>
    <Step number={1} title="Download the installer">
      <p className={styles.stepDescription}>
        Download the installer for each type of Mac you manage, Apple Silicon (M1 and later) or Intel, and upload it to
        your MDM.
      </p>
      {downloads.map(({ label, platform }) => (
        <DownloadRow
          key={platform}
          label={label}
          platform={platform}
          version={results[platform].data}
          isLoading={results[platform].isLoading}
          isError={results[platform].isError}
        />
      ))}
    </Step>

    <Step number={2} title="Permissions">
      <p className={styles.stepDescription}>
        Upload this profile to your MDM so users don&rsquo;t have to grant Accessibility manually.
      </p>
      <FileRow label="Accessibility profile" sublabel="(.mobileconfig)" href={ACCESSIBILITY_PROFILE_URL} />
    </Step>

    <Step number={3} title="Choose how devices update">
      <p className={styles.stepDescription}>
        To control how often Macs check for updates, download this file. <FrequencyHint />
      </p>
      <FileRow label="Update policy" sublabel="(.mobileconfig)" href={MAC_UPDATE_POLICY_URL} />
    </Step>

    <Step number={4} title="Fix app ownership after MDM install">
      <p className={styles.stepDescription}>
        Run this script after the MDM install so users aren&rsquo;t asked for admin credentials on every launch.
      </p>
      <FileRow label="Restore app ownership" sublabel="(.zsh)" href={RESTORE_OWNERSHIP_SCRIPT_URL} />
    </Step>
  </>
)

const WindowsSetup = ({ downloads, results }: PlatformDownloads) => (
  <>
    <Step number={1} title="Download the installer">
      <p className={styles.stepDescription}>
        Download the machine-wide MSI and upload it to your MDM. It is for 64-bit Windows only, and there is no ARM64
        build.
      </p>
      {downloads.map(({ label, platform }) => (
        <DownloadRow
          key={platform}
          label={label}
          platform={platform}
          version={results[platform].data}
          isLoading={results[platform].isLoading}
          isError={results[platform].isError}
        />
      ))}
    </Step>

    <Step number={2} title="Permissions">
      <p className={styles.stepDescription}>
        No permission profile is needed. Don&rsquo;t force the microphone privacy setting to Deny, or dictation
        can&rsquo;t start.
      </p>
    </Step>

    <Step number={3} title="Choose how devices update">
      <p className={styles.stepDescription}>
        <strong>Devices update themselves by default.</strong> The app restarts itself when the user is idle, and never
        during dictation or a meeting.
      </p>
      <p className={styles.stepDescription}>
        To choose which version devices run and when, set the MSI property{' '}
        <code className={styles.code}>AUTOUPDATE=0</code> in your MDM&rsquo;s install arguments or properties field, or
        use <code className={styles.code}>msiexec /i &quot;Wispr Flow-v&lt;version&gt;.msi&quot; /qn AUTOUPDATE=0</code>{' '}
        if your MDM requires a full command. The <FullGuideLink /> has provider-specific steps.
      </p>
      <p className={styles.stepDescription}>
        To control how often devices check for updates, download this file. <FrequencyHint />
      </p>
      <FileRow label="Update policy" sublabel="(.ps1)" href={WIN_UPDATE_POLICY_URL} />
    </Step>
  </>
)

const EnterpriseDownloads = () => {
  const [activeTab, setActiveTab] = useState(defaultTab)
  const downloadQueries = useQueries({
    queries: DOWNLOADS.map(({ queryKey, queryFn }) => ({
      queryKey,
      queryFn,
      staleTime: RELEASE_STALE_TIME_MS,
    })),
  })

  const results = Object.fromEntries(
    DOWNLOADS.map(({ platform }, index) => [
      platform,
      {
        data: downloadQueries[index].data,
        isLoading: downloadQueries[index].isLoading,
        isError: downloadQueries[index].isError,
      },
    ])
  ) as PlatformDownloads['results']

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <div className={styles.description}>
          See the <FullGuideLink /> for provider-specific steps (Jamf, Intune, Rippling, etc.)
        </div>

        <div className={styles.tabs}>
          <Tabs tabs={TABS} activeTab={activeTab} setActiveTab={setActiveTab} />
        </div>
      </div>

      <div className={styles.panel} role="tabpanel" aria-label={`${TABS[activeTab]} setup`}>
        {activeTab === WINDOWS_TAB ? (
          <WindowsSetup downloads={DOWNLOADS.filter(({ platform }) => platform === 'win-msi')} results={results} />
        ) : (
          <MacSetup downloads={DOWNLOADS.filter(({ platform }) => platform !== 'win-msi')} results={results} />
        )}
      </div>
    </div>
  )
}

export default EnterpriseDownloads
